import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  getUserFollowers,
  getUserFollowing,
  getShopFollowers,
} from "../../utility/httpActions/followListActions";
import { resolveImageUri } from "../../utility/constants";
import Avatar from "../../common-components/Avatar";
import { buildShopPath, buildUserPath } from "../../utility/utilityFunctions";
import useAuthentication from "../../hooks/useAuthentication";
import useGoToLogin from "../../hooks/useGoToLogin";
import { useFollowUser, useFollowShop } from "../../hooks/useFollowActions";
import UnfollowConfirmDialog from "../../common-components/UnfollowConfirmDialog";

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 350;

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const useFollowListQuery = (fetcher, id, filters) =>
  useInfiniteQuery({
    queryKey: [fetcher.name, id, filters],
    queryFn: ({ pageParam = 1 }) =>
      fetcher(id, { page: pageParam, limit: PAGE_SIZE, ...filters }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined,
    enabled: !!id,
    retry: false,
  });

// ── Entity rows ────────────────────────────────────────────────────

const FollowButton = ({ isFollowing, isPending, onClick }) => (
  <Button
    variant={isFollowing ? "outlined" : "contained"}
    size="small"
    disabled={isPending}
    onClick={onClick}
    sx={{
      borderRadius: 3,
      fontSize: 12,
      fontWeight: 600,
      px: 1.5,
      py: 0.25,
      minWidth: 80,
      textTransform: "none",
      whiteSpace: "nowrap",
      flexShrink: 0,
      ...(isFollowing
        ? { borderColor: "#046287", color: "#046287" }
        : { bgcolor: "#046287", "&:hover": { bgcolor: "#035070" } }),
    }}
  >
    {isPending ? "…" : isFollowing ? "Following" : "Follow"}
  </Button>
);

const UserRow = ({ entity, currentUserId, isLoggedIn }) => {
  const goToLogin = useGoToLogin();
  const isYou = entity.id?.toString() === currentUserId?.toString();
  const { mutate: toggleFollow, isPending } = useFollowUser(entity.id);
  const [unfollowConfirmOpen, setUnfollowConfirmOpen] = useState(false);
  const avatarUri = entity.profilePhoto?.path
    ? resolveImageUri(entity.profilePhoto)
    : null;

  return (
    <>
      <Box
        component={Link}
        to={buildUserPath(entity)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #f3f4f6",
          textDecoration: "none",
          color: "inherit",
          "&:hover": { bgcolor: "#fafafa" },
          "&:last-child": { borderBottom: "none" },
        }}
      >
        <Avatar src={avatarUri} name={entity.name} size={48} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {entity.name}
            </Typography>
            {isYou && (
              <Typography variant="caption" color="text.disabled">
                (You)
              </Typography>
            )}
            {!isYou && entity.followsViewer && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  bgcolor: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: 10,
                  px: 0.75,
                  py: 0.1,
                }}
              >
                <Typography
                  sx={{ fontSize: 11, color: "#1d4ed8", fontWeight: 600 }}
                >
                  Follows you
                </Typography>
              </Box>
            )}
          </Box>
          {entity.followersCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {entity.followersCount.toLocaleString()}{" "}
              {entity.followersCount === 1 ? "follower" : "followers"}
            </Typography>
          )}
        </Box>
        {!isYou && (
          <FollowButton
            isFollowing={entity.isFollowedByViewer}
            isPending={isPending}
            onClick={(e) => {
              e.preventDefault();
              if (!isLoggedIn) {
                goToLogin();
                return;
              }
              if (entity.isFollowedByViewer) {
                setUnfollowConfirmOpen(true);
              } else {
                toggleFollow(false);
              }
            }}
          />
        )}
      </Box>
      <UnfollowConfirmDialog
        open={unfollowConfirmOpen}
        onClose={() => setUnfollowConfirmOpen(false)}
        onConfirm={() => {
          toggleFollow(true);
          setUnfollowConfirmOpen(false);
        }}
        entityName={entity.name}
        isPending={isPending}
      />
    </>
  );
};

const ShopRow = ({ entity, isLoggedIn }) => {
  const goToLogin = useGoToLogin();
  const { mutate: toggleFollow, isPending } = useFollowShop(entity.id);
  const [unfollowConfirmOpen, setUnfollowConfirmOpen] = useState(false);
  const avatarUri = entity.profilePhoto?.path
    ? resolveImageUri(entity.profilePhoto)
    : null;

  return (
    <>
      <Box
        component={Link}
        to={buildShopPath(entity)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #f3f4f6",
          textDecoration: "none",
          color: "inherit",
          "&:hover": { bgcolor: "#fafafa" },
          "&:last-child": { borderBottom: "none" },
        }}
      >
        <Avatar src={avatarUri} name={entity.name} size={48} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {entity.name}
          </Typography>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}
          >
            <StorefrontOutlinedIcon sx={{ fontSize: 12, color: "#9ca3af" }} />
            <Typography variant="caption" color="text.secondary">
              Shop
              {entity.followersCount > 0 && (
                <>
                  {" "}
                  · {entity.followersCount.toLocaleString()}{" "}
                  {entity.followersCount === 1 ? "follower" : "followers"}
                </>
              )}
            </Typography>
          </Box>
        </Box>
        <FollowButton
          isFollowing={entity.isFollowedByViewer}
          isPending={isPending}
          onClick={(e) => {
            e.preventDefault();
            if (!isLoggedIn) {
              goToLogin();
              return;
            }
            if (entity.isFollowedByViewer) {
              setUnfollowConfirmOpen(true);
            } else {
              toggleFollow(false);
            }
          }}
        />
      </Box>
      <UnfollowConfirmDialog
        open={unfollowConfirmOpen}
        onClose={() => setUnfollowConfirmOpen(false)}
        onConfirm={() => {
          toggleFollow(true);
          setUnfollowConfirmOpen(false);
        }}
        entityName={entity.name}
        isPending={isPending}
      />
    </>
  );
};

const EntityRow = ({ entity, currentUserId, isLoggedIn }) =>
  entity.type === "shop" ? (
    <ShopRow entity={entity} isLoggedIn={isLoggedIn} />
  ) : (
    <UserRow
      entity={entity}
      currentUserId={currentUserId}
      isLoggedIn={isLoggedIn}
    />
  );

// ── Tab content ──────────────────────────────────────────────────

const ListTab = ({ queryResult, currentUserId, isLoggedIn }) => {
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = queryResult;

  const sentinelRef = useRef(null);

  const items = useMemo(
    () => data?.pages?.flatMap((p) => p.items ?? []) ?? [],
    [data],
  );

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#046287" }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 8,
          gap: 1,
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 36, color: "#d1d5db" }} />
        <Typography variant="body2" color="text.secondary">
          This list is private.
        </Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <Typography variant="body2" color="text.secondary">
          Nothing here yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {items.map((entity) => (
        <EntityRow
          key={`${entity.type ?? "user"}-${entity.id}`}
          entity={entity}
          currentUserId={currentUserId}
          isLoggedIn={isLoggedIn}
        />
      ))}
      <Box
        ref={sentinelRef}
        sx={{ py: 3, display: "flex", justifyContent: "center" }}
      >
        {isFetchingNextPage && (
          <CircularProgress size={24} sx={{ color: "#046287" }} />
        )}
      </Box>
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────

const FollowListPage = ({ initialTab = "followers" }) => {
  const { userId, shopId } = useParams();
  const { currentUser, isLoggedIn } = useAuthentication();

  const [tab, setTab] = useState(initialTab);
  const [searchInput, setSearchInput] = useState("");
  const debouncedQ = useDebounced(searchInput, DEBOUNCE_MS);

  const filters = debouncedQ ? { q: debouncedQ } : {};
  const id = userId || shopId;

  const followersResult = useFollowListQuery(
    userId ? getUserFollowers : getShopFollowers,
    id,
    filters,
  );
  const followingResult = useFollowListQuery(
    getUserFollowing,
    userId || null,
    filters,
  );

  const followerCount = followersResult.data?.pages?.[0]?.totalItems ?? 0;
  const followingCount = followingResult.data?.pages?.[0]?.totalItems ?? 0;

  const isShop = !!shopId;

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", py: 3 }}>
      {/* Search */}
      <Box sx={{ px: 2, mb: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f9fafb" },
          }}
        />
      </Box>

      {/* Tabs — users only */}
      {!isShop && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            borderBottom: "1px solid #e5e7eb",
            mb: 0,
            "& .MuiTab-root": {
              fontWeight: 600,
              textTransform: "none",
              minWidth: 100,
            },
            "& .Mui-selected": { color: "#046287" },
            "& .MuiTabs-indicator": { bgcolor: "#046287" },
          }}
        >
          <Tab
            value="followers"
            label={
              followerCount > 0
                ? `Followers (${followerCount.toLocaleString()})`
                : "Followers"
            }
          />
          <Tab
            value="following"
            label={
              followingCount > 0
                ? `Following (${followingCount.toLocaleString()})`
                : "Following"
            }
          />
        </Tabs>
      )}

      {/* List */}
      {isShop || tab === "followers" ? (
        <ListTab
          queryResult={followersResult}
          currentUserId={currentUser?.id}
          isLoggedIn={isLoggedIn}
        />
      ) : (
        <ListTab
          queryResult={followingResult}
          currentUserId={currentUser?.id}
          isLoggedIn={isLoggedIn}
        />
      )}
    </Box>
  );
};

export default FollowListPage;
